import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as UpgradeToOrgModalIcon } from '@assets/icons/upgradeToOrg.svg';
import { Button, Modal } from '@components/index';
import { AccountType, OrgStatus, UserFlow } from '@constants/generic';
import { AUTH } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import {
  useCreateOrganizationMutation,
  useOnboardOrganizationMutation
} from '@features/authentication/queries/AuthQuery';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';
import {
  getLocalStorageItem,
  setMultipleLocalStorageItems
} from '@utils/storage';
import { Org } from 'types/organization';

interface UpgradeToOrgModalProps {
  isOpen: boolean;
  onCancelUpgradeToOrg: () => void;
  orgList: Org[];
  setUpgradeToOrgModalOpen: (isOpen: boolean) => void;
}

const UpgradeToOrgModal = ({
  isOpen,
  onCancelUpgradeToOrg,
  orgList,
  setUpgradeToOrgModalOpen
}: UpgradeToOrgModalProps) => {
  const orgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const navigate = useNavigate();
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { currentUser } = useSelector((state: any) => state.user);
  const currentOrg = orgList?.find((org: Org) => org.id === orgId);

  const [onboardOrganization] = useOnboardOrganizationMutation();
  const [createOrganization, { isLoading: isCreateOrgLoading }] =
    useCreateOrganizationMutation();

  useEffect(() => {
    sendMixpanelEventV2(MixpanelEventV2.UPGRADE_TO_ORG_MODAL_VIEWED, {
      mobile_number: currentUser?.mobile || '',
      profile_name: currentUser?.name || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgradeIndividualLiteToOrgLite = async () => {
    const requestBody = {
      organizationId: orgId,
      body: {
        branch_name: currentOrg?.default_branch_name.trim(),
        organisation_name: currentOrg?.name.trim(),
        branch_id: currentOrg?.default_branch_id,
        is_lite: true
      }
    };
    const response = await onboardOrganization(requestBody);
    if ('data' in response) {
      createSuccessAlert('Updated to organization successfully');
      setUpgradeToOrgModalOpen(true);
      window.location.reload();
    } else {
      const error = response.error as ApiError;
      createErrorAlert(
        error?.data?.message || 'Error in upgrading to organization'
      );
    }
  };

  const createNewOrg = async () => {
    const response = await createOrganization(null);
    if ('data' in response) {
      setMultipleLocalStorageItems([
        {
          key: STORAGE_KEYS.UPGRADED_ORG_ID,
          value: response.data?.organisation_id
        },
        {
          key: STORAGE_KEYS.UPGRADED_BRANCH_ID,
          value: response.data?.branch_id
        },
        { key: STORAGE_KEYS.USER_FLOW, value: UserFlow.UPGRADE_TO_ORG }
      ]);
      navigate(AUTH.SELECT_ACCOUNT);
    } else if ('error' in response) {
      const error = response.error as ApiError;
      createErrorAlert(error.data?.message);
    }
    setUpgradeToOrgModalOpen(false);
  };

  const onConfirmUpgradeToOrg = () => {
    sendMixpanelEvent(Event.UPGRADE_TO_ORGANIZATION_CONFIRM_BUTTON_CLICKED, {
      individual_organization_name: currentUser?.name || ''
    });
    sendMixpanelEventV2(MixpanelEventV2.UPGRADE_TO_ORG_CONFIRMED, {
      mobile_number: currentUser?.mobile || '',
      profile_name: currentUser?.name || ''
    });
    if (
      currentOrg?.status === OrgStatus.KYC_PENDING &&
      currentOrg?.type === AccountType.INDIVIDUAL &&
      !currentOrg?.is_upgrade_requested &&
      !currentOrg?.is_kyc_deferred
    ) {
      upgradeIndividualLiteToOrgLite();
    } else createNewOrg();
  };

  return (
    <Modal
      isOpen={isOpen}
      isClickOutsideRequired={false}
      className="w-112 max-w-500 !bg-cofeeNeutral1 p-6"
    >
      <div className="flex w-full flex-col items-center justify-center">
        <div className="flex w-full justify-center text-lg font-semibold text-content">
          Upgrade to Organization
        </div>
        <UpgradeToOrgModalIcon className="mt-2.5" />
        <div className="mt-2.5 flex w-full justify-center text-center text-sm font-normal text-grey">
          Best for institutes with multiple branches. Use to collect fees, give
          role-based access, manage fees, and track everything with a dashboard.
        </div>
        <div className="mt-6 flex w-full gap-x-4">
          <Button
            label="Cancel"
            variant="tertiary"
            className="w-1/2"
            handleClick={onCancelUpgradeToOrg}
          />
          <Button
            label="Proceed"
            className="w-1/2"
            handleClick={onConfirmUpgradeToOrg}
            loading={isCreateOrgLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeToOrgModal;
